import { useCallback } from "react";

import { useCurrentOrganizationContext } from "@/contexts/CurrentOrganizationContext";
import { useCurrentPlatformContext } from "@/contexts/CurrentPlatformContext";
import {
  buildScopedPath,
  isAlreadyScopedPath,
  isExternalOrAnchorHref,
} from "@/utils/common/scoped-routes";

/**
 * Returns a builder that turns an internal absolute path (e.g. "/users") into
 * the scoped equivalent when the current platform is known. It injects only the
 * platform + organization (tenant) scope: platform-only (`/platform/:p/...`) or
 * org (`/platform/:p/entity/:o/...`).
 *
 * The league is a page-specific, deeper scope that is **always carried
 * explicitly in the path** (`/league/:id/...` — the league sidebar nav, the
 * `LeaguePageRedirect`), never auto-injected from the current context. That way
 * an org-level link like `/tours` correctly **leaves** the active league instead
 * of staying nested under it (`…/league/:l/tours`), and an explicit
 * `/league/:id/...` path resolves to `/platform/:p/entity/:o/league/:id/...`
 * without doubling the segment.
 *
 * External and anchor URLs pass through, as do paths that are already scoped.
 */
export const useScopedHref = () => {
  const { platform } = useCurrentPlatformContext();
  const { organization } = useCurrentOrganizationContext();
  const platformId = platform?.id ?? null;
  const organizationId = organization?.id ?? null;

  return useCallback(
    (href: string): string => {
      if (!href) return href;
      if (isExternalOrAnchorHref(href)) return href;
      if (isAlreadyScopedPath(href)) return href;
      if (platformId === null) return href;
      if (!href.startsWith("/")) return href;
      return buildScopedPath(platformId, organizationId, null, href.replace(/^\/+/, ""));
    },
    [platformId, organizationId]
  );
};
